/**
 * Global roughness calibration (admin): benchmark runs with assigned scores.
 * Stored in Firestore appConfig/roughness — read by all signed-in riders.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { benchmarksToAnchors, DEFAULT_ROUGHNESS_ANCHORS } from './roughnessScore';

export const ROUGHNESS_CALIBRATION_DOC = ['appConfig', 'roughness'];

let cachedCalibration = null;
let loadPromise = null;

export function getDefaultRoughnessCalibration() {
  return {
    benchmarks: [],
    anchors: DEFAULT_ROUGHNESS_ANCHORS,
    updatedAt: null,
  };
}

function normalizeBenchmark(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const runId = typeof entry.runId === 'string' ? entry.runId : null;
  const assignedScore = Number(entry.assignedScore);
  const raw = Number(entry.raw);
  if (!runId || !Number.isFinite(assignedScore) || !Number.isFinite(raw)) return null;
  return {
    id: typeof entry.id === 'string' ? entry.id : `bp-${runId}-${assignedScore}`,
    runId,
    runLabel: typeof entry.runLabel === 'string' ? entry.runLabel : '',
    assignedScore: Math.max(0, Math.min(200, assignedScore)),
    raw,
    updatedAt: entry.updatedAt ?? null,
  };
}

export function normalizeRoughnessCalibration(data) {
  if (!data || typeof data !== 'object') return getDefaultRoughnessCalibration();
  const benchmarks = (Array.isArray(data.benchmarks) ? data.benchmarks : [])
    .map(normalizeBenchmark)
    .filter(Boolean);
  const anchors = benchmarks.length >= 2 ? benchmarksToAnchors(benchmarks) : DEFAULT_ROUGHNESS_ANCHORS;
  return {
    benchmarks,
    anchors,
    updatedAt: data.updatedAt ?? null,
  };
}

/**
 * @returns {Promise<{ benchmarks: object[], anchors: {raw:number,score:number}[], updatedAt: any }>}
 */
export async function fetchRoughnessCalibration() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const ref = doc(db, ROUGHNESS_CALIBRATION_DOC[0], ROUGHNESS_CALIBRATION_DOC[1]);
      const snap = await getDoc(ref);
      const cal = snap.exists() ? normalizeRoughnessCalibration(snap.data()) : getDefaultRoughnessCalibration();
      cachedCalibration = cal;
      return cal;
    } catch (e) {
      console.warn('fetchRoughnessCalibration failed:', e);
      const fallback = getDefaultRoughnessCalibration();
      cachedCalibration = fallback;
      return fallback;
    } finally {
      loadPromise = null;
    }
  })();
  return loadPromise;
}

/** Sync read after at least one fetch; otherwise defaults. */
export function getRoughnessCalibrationSync() {
  return cachedCalibration ?? getDefaultRoughnessCalibration();
}

export function invalidateRoughnessCalibrationCache() {
  cachedCalibration = null;
  loadPromise = null;
}

/**
 * @param {{ benchmarks: object[] }} payload
 */
export async function saveRoughnessCalibration(payload) {
  const benchmarks = (Array.isArray(payload.benchmarks) ? payload.benchmarks : [])
    .map(normalizeBenchmark)
    .filter(Boolean);
  const ref = doc(db, ROUGHNESS_CALIBRATION_DOC[0], ROUGHNESS_CALIBRATION_DOC[1]);
  await setDoc(
    ref,
    {
      benchmarks,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  const cal = normalizeRoughnessCalibration({ benchmarks, updatedAt: new Date() });
  cachedCalibration = cal;
  return cal;
}

export function newBenchmarkId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bp-${Date.now()}`;
}
